import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, SCHEDULE_MEETING_TABS, SCHEDULE_MEETING_COLUMNS,
  SCHEDULE_MEETING_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl,
  SCHEDULE_MEETING_DUPLICATE_CHECK, findDuplicates,
  SCHEDULE_MEETING_DEFAULTS, SCHEDULE_MEETING_EMAIL_DEFAULTS,
} from "./constants/adminConstants";
import { resolveIcon } from "../Main/Footer/FooterData";
import { compressImageFile, validateImageFile } from "./compressImage";
import { uploadToCloudinary } from "./uploadImage";
import "./css/adminStyles.css";

function AdminScheduleMeetingEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("meetingTypes");
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const [pendingIconUpload, setPendingIconUpload] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const searchRef = useRef(null);
  const snapshotRef = useRef(null);
  const dragRef = useRef(null);
  const fileInputRef = useRef(null);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const tabDragRef = useRef(null);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
  }, [activeTab]);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const raw = data.section.content;
        const seeded = { ...raw };
        for (const key of Object.keys(SCHEDULE_MEETING_DEFAULTS)) {
          if (!seeded[key] || (Array.isArray(seeded[key]) && seeded[key].length === 0)) {
            seeded[key] = SCHEDULE_MEETING_DEFAULTS[key];
          }
        }
        // Merge emailConfig defaults so admin fields are never empty
        seeded.emailConfig = { ...SCHEDULE_MEETING_EMAIL_DEFAULTS, ...(seeded.emailConfig || {}) };
        setContent(seeded);
        snapshotRef.current = JSON.stringify(seeded);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(SCHEDULE_MEETING_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(SCHEDULE_MEETING_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/pull-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      arr[index] = { ...(arr[index] || {}), [key]: value };
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowBefore = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      const columns = SCHEDULE_MEETING_COLUMNS[activeTab] || [];
      for (const col of columns) {
        emptyRow[col.key] = "";
      }
      arr.splice(index, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowAfter = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      const columns = SCHEDULE_MEETING_COLUMNS[activeTab] || [];
      for (const col of columns) {
        emptyRow[col.key] = "";
      }
      arr.splice(index + 1, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      updated[field] = (updated[field] || []).filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleIconUploadClick = (field, index, key) => {
    setPendingIconUpload({ field, index, key });
    fileInputRef.current?.click();
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingIconUpload) return;
    const check = validateImageFile(file);
    if (!check.valid) { alert(check.message); e.target.value = ""; return; }
    compressImageFile(file, 48, 0.8).then(async (dataUrl) => {
      try {
        const url = await uploadToCloudinary(dataUrl, file.name);
        handleArrayChange(pendingIconUpload.field, pendingIconUpload.index, pendingIconUpload.key, url);
      } catch (err) {
        alert("Upload failed: " + err.message);
      }
      setPendingIconUpload(null);
    });
    e.target.value = "";
  };

  const handleIconPresetSelect = (field, index, key, kindKey, preset) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      arr[index] = { ...arr[index], [key]: preset, [kindKey]: "" };
      updated[field] = arr;
      return updated;
    });
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, SCHEDULE_MEETING_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => b.duplicateIndex - a.duplicateIndex);
    for (const dup of sorted) {
      const arr = [...(cleanContent[dup.field] || [])];
      if (arr[dup.duplicateIndex]) {
        arr.splice(dup.duplicateIndex, 1);
        cleanContent[dup.field] = arr;
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    const submitPayload = overrideContent || { ...content };
    const estimateSize = new Blob([JSON.stringify(submitPayload)]).size;
    if (estimateSize > 4 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderEmailConfig = () => {
    const cfg = content?.emailConfig || {};
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Email Configuration</h4>
        </div>
        <div className="AdminScheduleGrid2ColGap24">
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Customer Notification Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the customer email when someone books a meeting.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Meeting Booking Confirmed — TheVisaCo"
                value={cfg.meetingSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), meetingSubject: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt2">Variables: <code>{`{{ humanId }}`}</code>, <code>{`{{ name }}`}</code>, <code>{`{{ meetingType }}`}</code></p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Customer Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH300"
                placeholder="Hi {{ name }},..."
                value={cfg.meetingBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), meetingBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>, <code>{`{{ adminUrl }}`}</code>
              </p>
            </div>
          </div>

          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Admin Notification Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the admin email when someone books a meeting.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Admin Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="New Meeting Booking — {{ humanId }}"
                value={cfg.adminMeetingSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminMeetingSubject: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt2">Variables: <code>{`{{ humanId }}`}</code>, <code>{`{{ name }}`}</code>, <code>{`{{ meetingType }}`}</code></p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Admin Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH300"
                placeholder="New meeting booked by {{ name }}..."
                value={cfg.adminMeetingBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminMeetingBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Variables: <code>{`{{ humanId }}`}</code>, <code>{`{{ name }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>, <code>{`{{ adminUrl }}`}}</code>
              </p>
            </div>
          </div>
        </div>
        <p className="AdminScheduleText12Gray">
          <strong>From Email &amp; From Name</strong> are configured globally in <a href="/admin/email-settings" className="AdminScheduleLinkUnderline">Email Settings</a>. Changes there apply everywhere.
        </p>
      </div>
    );
  };

  const renderMeetLinkEmail = () => {
    const cfg = content?.emailConfig || {};
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Google Meet Link Email</h4>
        </div>
        <div className="AdminScheduleGrid2ColGap24">
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Customer Meet Link Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the customer when admin sets the Google Meet link.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Google Meet Link Available for Your Meeting — TheVisaCo"
                value={cfg.meetLinkSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), meetLinkSubject: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt2">Variables: <code>{`{{ humanId }}`}</code>, <code>{`{{ name }}`}</code>, <code>{`{{ meetingType }}`}</code></p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Customer Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH200"
                placeholder="Hi {{ name }},..."
                value={cfg.meetLinkBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), meetLinkBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>, <code>{`{{ meetLink }}`}</code>
              </p>
            </div>
          </div>
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Admin Meet Link Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the admin when they set the Google Meet link.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Admin: Google Meet Link Set — {{ humanId }}"
                value={cfg.adminMeetLinkSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminMeetLinkSubject: e.target.value },
                }))}
              />
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Admin Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH200"
                placeholder="Google Meet link has been set..."
                value={cfg.adminMeetLinkBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminMeetLinkBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>, <code>{`{{ meetLink }}`}</code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNoShowEmail = () => {
    const cfg = content?.emailConfig || {};
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">No Show / Missed Meeting Email</h4>
        </div>
        <div className="AdminScheduleGrid2ColGap24">
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Customer No-Show Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the customer when they miss their meeting.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="No Show — Meeting Missed — {{ humanId }}"
                value={cfg.noShowSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), noShowSubject: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt2">Variables: <code>{`{{ humanId }}`}</code>, <code>{`{{ name }}`}</code>, <code>{`{{ meetingType }}`}</code></p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Customer Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH200"
                placeholder="Hi {{ name }},..."
                value={cfg.noShowBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), noShowBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>
              </p>
            </div>
          </div>
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Admin No-Show Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the admin when a customer misses their meeting.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Admin: No Show — {{ humanId }}"
                value={cfg.adminNoShowSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminNoShowSubject: e.target.value },
                }))}
              />
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Admin Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH200"
                placeholder="No Show — {{ humanId }}..."
                value={cfg.adminNoShowBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminNoShowBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCancelEmail = () => {
    const cfg = content?.emailConfig || {};
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">Cancel Meeting Email</h4>
        </div>
        <div className="AdminScheduleGrid2ColGap24">
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Customer Cancel Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the customer when their meeting is cancelled.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Meeting Cancelled — {{ humanId }}"
                value={cfg.cancelSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), cancelSubject: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt2">Variables: <code>{`{{ humanId }}`}</code>, <code>{`{{ name }}`}</code>, <code>{`{{ meetingType }}`}</code></p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Customer Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH200"
                placeholder="Hi {{ name }},..."
                value={cfg.cancelBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), cancelBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>
              </p>
            </div>
          </div>
          <div className="AdminScheduleFlexColumnGap16">
            <div>
              <h4 className="AdminSectionTitle AdminScheduleText14Mb4">Admin Cancel Email</h4>
              <p className="AdminText12 AdminTextMuted AdminM0">Sent to the admin when a meeting is cancelled.</p>
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Email Subject</label>
              <input
                className="AdminInput"
                type="text"
                placeholder="Admin: Meeting Cancelled — {{ humanId }}"
                value={cfg.adminCancelSubject || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminCancelSubject: e.target.value },
                }))}
              />
            </div>
            <div>
              <label className="AdminLabel AdminMb4">Admin Email Body / Draft</label>
              <textarea
                className="AdminInput AdminScheduleMinH200"
                placeholder="Meeting Cancelled — {{ humanId }}..."
                value={cfg.adminCancelBody || ""}
                onChange={(e) => setContent((prev) => ({
                  ...prev,
                  emailConfig: { ...(prev.emailConfig || {}), adminCancelBody: e.target.value },
                }))}
              />
              <p className="AdminText12 AdminTextMuted AdminScheduleMt4">
                Available variables: <code>{`{{ name }}`}</code>, <code>{`{{ humanId }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ phone }}`}</code>, <code>{`{{ meetingType }}`}</code>, <code>{`{{ meetingDate }}`}</code>, <code>{`{{ meetingSlot }}`}</code>, <code>{`{{ durationMinutes }}`}</code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderArrayEditor = (field, columns, customTitle) => {
    const items = content?.[field] || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {customTitle || field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div className="AdminFlexRowGap8 AdminFlexShrink0">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddRowAfter(field, items.length - 1)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <div className="AdminTableScroll">
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === "icon" ? (
                        <div className="AdminIconCell">
                          <span className="AdminIconPreview">
                            {resolveIcon(item[col.key])}
                          </span>
                          <input
                            className="AdminInput AdminTableInput"
                            value={item[col.kindKey || col.key] || ""}
                            onChange={(e) => handleArrayChange(field, idx, col.kindKey || col.key, e.target.value)}
                            placeholder={col.label}
                          />
                          <select
                            className="AdminInput AdminTableInput AdminScheduleMinW90MaxW120"
                            value=""
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) handleIconPresetSelect(field, idx, col.key, col.kindKey, v);
                            }}
                          >
                            <option value="">Presets...</option>
                            {["🎓", "📄", "🎤", "📋", "⭐", "💼", "✈️", "🛂", "📝", "👥", "📞", "🖥️", "✅", "🔒", "🌍", "⚡"].map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                          <button
                            className="AdminIconUploadBtn"
                            title="Upload icon image"
                            onClick={() => handleIconUploadClick(field, idx, col.key)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                            </svg>
                          </button>
                          {item[col.key] && (
                            <button
                              className="AdminIconBtn AdminIconBtnRed AdminScheduleClearBtn"
                              title="Clear"
                              onClick={() => handleArrayChange(field, idx, col.key, "")}
                            >
                              &#10005;
                            </button>
                          )}
                        </div>
                      ) : col.type === "textarea" ? (
                        <textarea
                          className="AdminInput AdminTableInput AdminScheduleMinH60"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      ) : col.type === "select" ? (
                        <select
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                        >
                          {(col.options || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <div className="AdminEmptyState">{SCHEDULE_MEETING_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{SCHEDULE_MEETING_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{SCHEDULE_MEETING_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Schedule Meeting Content
          {activeTab !== "data" && Array.isArray(content?.[activeTab]) && (
            <span className="AdminCountBadge">{content[activeTab].length}</span>
          )}
        </h3>
        <div className="AdminHeaderActionsRow">
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn AdminNowrap" onClick={handleSubmit} disabled={!isDirty || saving}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      <input type="file" accept="image/png,image/gif,image/jpeg,image/webp" ref={fileInputRef} className="AdminHidden" onChange={handleIconFileChange} />

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminFlexRowGap8" style={{ marginBottom: 12, alignItems: "center" }}>
        <button className="AdminTabHamburger" onClick={() => setShowMobileMenu(true)} title="Menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div
          className="AdminTabBar"
          ref={tabBarRef}
          style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 0 }}
        >
          {SCHEDULE_MEETING_TABS.map((tab) => (
            <button
              key={tab.id}
              draggable
              onDragStart={(e) => { tabDragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const fromId = tabDragRef.current;
                if (!fromId || fromId === tab.id) return;
                setActiveTab(tab.id);
              }}
              onDragEnd={(e) => { e.target.style.opacity = '1'; tabDragRef.current = null; }}
              className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
                color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
                borderRadius: "8px 8px 0 0", transition: "all 0.2s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {showMobileMenu && (
        <>
          <div className="AdminBottomSheetOverlay" onClick={() => setShowMobileMenu(false)} />
          <div className="AdminBottomSheet">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <strong style={{ fontSize: 16, color: "#1B2A4A" }}>Tabs</strong>
              <button className="AdminCloseBtn" onClick={() => setShowMobileMenu(false)}>&times;</button>
            </div>
            <div
              className="AdminTabBar"
              style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0" }}
            >
              {SCHEDULE_MEETING_TABS.map((tab) => (
                <button
                  key={tab.id}
                  draggable
                  onDragStart={(e) => { tabDragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromId = tabDragRef.current;
                    if (!fromId || fromId === tab.id) return;
                    setActiveTab(tab.id); setShowMobileMenu(false);
                  }}
                  onDragEnd={(e) => { e.target.style.opacity = '1'; tabDragRef.current = null; }}
                  className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
                  onClick={() => { setActiveTab(tab.id); setShowMobileMenu(false); }}
                  style={{
                    padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
                    color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
                    borderRadius: "8px 8px 0 0", transition: "all 0.2s",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">Data</h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">{section?.key || "scheduleMeeting"}</p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(SCHEDULE_MEETING_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "meetingTypes" && (
        <>
          {renderArrayEditor("meetingTypes", SCHEDULE_MEETING_COLUMNS.meetingTypes)}
          {renderArrayEditor("durationPrice", SCHEDULE_MEETING_COLUMNS.durationPrice)}
        </>
      )}

      {activeTab === "durationDateTime" && (
        <>
          {renderArrayEditor("durations", SCHEDULE_MEETING_COLUMNS.durations, "Meeting Duration")}
          {renderArrayEditor("slots", SCHEDULE_MEETING_COLUMNS.slots, "Available Time Slots")}
        </>
      )}

      {activeTab === "contactDetails" && renderArrayEditor("contactDetails", SCHEDULE_MEETING_COLUMNS.contactDetails)}

      {activeTab === "countryCodes" && renderArrayEditor("countryCodes", SCHEDULE_MEETING_COLUMNS.countryCodes)}

      {activeTab === "consentAgreement" && (
        <>
          {renderArrayEditor("consentFree", SCHEDULE_MEETING_COLUMNS.consentFree, "Free Consent")}
          {renderArrayEditor("consentPaid", SCHEDULE_MEETING_COLUMNS.consentPaid, "Paid Consent")}
        </>
      )}

      {activeTab === "emailSettings" && (
        <>
          {renderEmailConfig()}
          {renderMeetLinkEmail()}
          {renderNoShowEmail()}
          {renderCancelEmail()}
        </>
      )}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminScheduleMaxW440">
            <h3 className="AdminModalTitle">Note for Approval</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              {user?.role === "Staff"
                ? "Your changes will be sent to the Manager for approval."
                : "Your changes will be sent to the Admin for approval."}
            </p>
            <textarea
              className="AdminNoteTextarea"
              placeholder="Describe what you changed and why..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="AdminNoteModalRow">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowNoteModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall" onClick={handleConfirmSubmit} disabled={!noteText.trim()}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminModalMaxW480" style={{position:"relative"}}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <h3 className="AdminModalTitle">Duplicate Rows Found</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B12">
              {duplicateInfo.length} duplicate row(s) detected. You can remove them or continue anyway.
            </p>
            {duplicateInfo.map((d, i) => (
              <div key={i} className="AdminDupWarningBox">
                <div className="AdminDupItem">
                  <strong>{d.tabLabel}</strong>: Row {d.duplicateIndex + 1} matches Row {d.existingIndex + 1}
                </div>
              </div>
            ))}
            <div className="AdminFlexCenterG12 AdminMt16">
              <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setShowDuplicateModal(false)}>Cancel</button>
              <button className="AdminButton AdminButtonSmall AdminButtonDanger" onClick={handleRemoveDuplicates} disabled={duplicateActionLoading}>
                {duplicateActionLoading ? "Removing..." : "Remove Duplicates"}
              </button>
              <button className="AdminButton AdminButtonSmall" onClick={handleContinueAnyway}>Continue Anyway</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent AdminModalMaxW400" style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle" style={{ marginBottom: 4 }}>{successTitle}</h3>
            <p className="AdminText14 AdminTextMuted" style={{ margin: "0 0 20px 0" }}>
              {successTitle === "Changes Published" ? "Changes are now live." : "Your request has been submitted for review."}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={() => setShowSuccessOverlay(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminScheduleMeetingEditor;
